import { useState, useEffect } from 'react';
import { FileText, Save, Eye, RotateCcw, Loader2, ShieldCheck, Bell } from 'lucide-react';
import { settingsService } from '../services/settingsService';
import transportContractDefault from '@/assets/contracts/transport_contract.txt?raw';
import disclosureTextDefault from '@/assets/contracts/disclosure_text.txt?raw';
import explicitConsentDefault from '@/assets/contracts/explicit_consent.txt?raw';
import electronicConsentDefault from '@/assets/contracts/electronic_consent.txt?raw';

interface ContractTab {
  key: string;
  title: string;
  description: string;
  previewTitle: string;
  icon: typeof FileText;
  defaultValue: string;
}

const TABS: ContractTab[] = [
  {
    key: 'driver_registration_contract',
    title: 'Kayıt Sözleşmesi',
    description: 'Sürücü kayıt formunda gösterilen Yurt İçi Taşımacılık Hizmet Sözleşmesi. Kayıt olabilmek için kabul edilmesi zorunludur.',
    previewTitle: 'Yurt İçi Taşımacılık Hizmet Sözleşmesi',
    icon: FileText,
    defaultValue: transportContractDefault,
  },
  {
    key: 'driver_disclosure_text',
    title: 'Aydınlatma Metni',
    description: 'KVKK kapsamında sürücü aydınlatma metni. /disclosure sayfasında görüntülenir.',
    previewTitle: 'Sürücü Aydınlatma Metni',
    icon: ShieldCheck,
    defaultValue: disclosureTextDefault,
  },
  {
    key: 'driver_explicit_consent_text',
    title: 'Açık Rıza Metni',
    description: 'Arka plan konum verisi kullanımına ilişkin KVKK açık rıza metni. /explicit-consent sayfasında ve kayıt formunda gösterilir.',
    previewTitle: 'Açık Rıza Metni',
    icon: ShieldCheck,
    defaultValue: explicitConsentDefault,
  },
  {
    key: 'driver_electronic_consent_text',
    title: 'Ticari İleti Onayı',
    description: 'SMS/WhatsApp ticari elektronik ileti onay metni. /electronic-consent sayfasında ve kayıt formunda gösterilir.',
    previewTitle: 'Ticari Elektronik İleti Onayı',
    icon: Bell,
    defaultValue: electronicConsentDefault,
  },
];

export function ContractManagement() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const activeTab = TABS[activeIndex];

  useEffect(() => {
    loadContent(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const loadContent = async (tab: ContractTab) => {
    setIsLoading(true);
    setError(null);
    setShowPreview(false);
    try {
      const value = await settingsService.getSettingValue<string>(tab.key, tab.defaultValue);
      setContent(value || tab.defaultValue);
      setSavedContent(value || tab.defaultValue);
    } catch (err: any) {
      console.error('Error loading contract:', err);
      setError(err.message || 'İçerik yüklenirken hata oluştu');
      setContent(tab.defaultValue);
      setSavedContent(tab.defaultValue);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await settingsService.saveSetting(activeTab.key, content, {
        type: 'text',
        category: activeTab.key === 'driver_registration_contract' ? 'contracts' : 'legal_texts',
        description: activeTab.title,
      });
      if (response.success) {
        setSavedContent(content);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err: any) {
      console.error('Error saving content:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (confirm(`${activeTab.title} varsayılan haline döndürmek istediğinize emin misiniz?`)) {
      setContent(activeTab.defaultValue);
      setSavedContent(activeTab.defaultValue);
    }
  };

  const hasChanges = content !== savedContent;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-gray-500">İçerik yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sözleşme ve Onay Metinleri Yönetimi</h1>
          <p className="text-gray-600">
            Sürücü kayıt formunda ve mobil uygulama sayfalarında gösterilen metinleri buradan düzenleyebilirsiniz.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex min-w-max gap-2">
            {TABS.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveIndex(index)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    index === activeIndex
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.title}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto">
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                hasChanges && !isSaving
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Kaydet
            </button>

            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Düzenleme Moduna Geç' : 'Önizleme'}
            </button>

            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Varsayılana Döndür
            </button>
          </div>

          {saved && (
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium">
              ✓ Kaydedildi!
            </div>
          )}

          {hasChanges && !saved && (
            <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg text-sm">
              ⚠️ Kaydedilmemiş değişiklikler var
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Editor / Preview */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
            {(() => {
              const Icon = activeTab.icon;
              return <Icon className="w-5 h-5 text-gray-600" />;
            })()}
            <h2 className="font-semibold text-gray-900">
              {showPreview ? `Önizleme - ${activeTab.previewTitle}` : `${activeTab.title} Düzenleme`}
            </h2>
          </div>

          {showPreview ? (
            <div className="p-6">
              <div className="max-w-md mx-auto bg-gray-50 rounded-xl border-2 border-gray-300 p-6">
                <div className="flex items-center gap-2 mb-4">
                  {(() => {
                    const Icon = activeTab.icon;
                    return <Icon className="w-6 h-6 text-blue-600" />;
                  })()}
                  <h3 className="font-bold text-lg text-gray-900">{activeTab.previewTitle}</h3>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 h-64 overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap">
                  {content}
                </div>

                {activeTab.key === 'driver_registration_contract' && (
                  <div className="mt-4 flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input
                      type="checkbox"
                      id="preview-contract"
                      className="mt-1 w-5 h-5 text-blue-600"
                    />
                    <label htmlFor="preview-contract" className="text-sm text-gray-900">
                      Yukarıdaki sözleşmeyi okudum, anladım ve <strong>kabul ediyorum</strong>
                    </label>
                  </div>
                )}

                <button
                  className="w-full mt-4 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed"
                  disabled
                >
                  🔒 Önizleme
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full h-[600px] p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Metni buraya yazın..."
              />

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">💡 İpuçları:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Metin olduğu gibi mobil kayıt formunda ve ilgili sayfalarda gösterilir</li>
                  <li>• Kayıt sözleşmesinde "X TC/VKN no'lu" ve "X adresinde mukim X" yer tutucuları sürücü bilgileriyle otomatik doldurulur</li>
                  <li>• Değişiklikler anında tüm yeni kayıtlarda geçerli olacak</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Karakter Sayısı</p>
            <p className="text-2xl font-bold text-gray-900">{content.length.toLocaleString('tr-TR')}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Satır Sayısı</p>
            <p className="text-2xl font-bold text-gray-900">{content.split('\n').length}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Kelime Sayısı</p>
            <p className="text-2xl font-bold text-gray-900">
              {content.split(/\s+/).filter(w => w.length > 0).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}